import useUserUiPreferenceStorageValue from "./useUserUiPreferenceStorageValue.js";
import React from "react";
import {
  FLOATING_MAP_INITIAL_POSITION,
  FLOATING_MAP_PANEL_INSET,
  FLOATING_MAP_SIZE_PRESETS,
  clampFloatingMapDocumentPosition,
  clampFloatingMapHeight,
  clampFloatingMapWidth,
  resolveAbsoluteFloatingMapAnchorPosition,
  resolveDefaultFloatingMapDocumentPosition,
  resolveFloatingMapDimensions,
} from "../utils/floatingRouteMapLayout.js";
import {
  clearPersistedFloatingRouteMapState,
  persistFloatingRouteMapState,
  readPersistedFloatingRouteMapState,
} from "../utils/floatingRouteMapStorage.js";

function resolveInitialPosition(initialPosition = FLOATING_MAP_INITIAL_POSITION) {
  return {
    x: Number.isFinite(Number(initialPosition?.x)) ? Number(initialPosition.x) : FLOATING_MAP_INITIAL_POSITION.x,
    y: Number.isFinite(Number(initialPosition?.y)) ? Number(initialPosition.y) : FLOATING_MAP_INITIAL_POSITION.y,
  };
}

function resolveInitialSize(initialSize = "compact", sizePresets = FLOATING_MAP_SIZE_PRESETS) {
  const resolvedSizePresets = sizePresets && typeof sizePresets === "object"
    ? sizePresets
    : FLOATING_MAP_SIZE_PRESETS;

  if (resolvedSizePresets[initialSize]) {
    return initialSize;
  }

  if (resolvedSizePresets.compact) {
    return "compact";
  }

  if (resolvedSizePresets.standard) {
    return "standard";
  }

  return Object.keys(resolvedSizePresets)[0] || "compact";
}

export default function useFleetFloatingMapState({
  initialPosition = FLOATING_MAP_INITIAL_POSITION,
  initialSize = "compact",
  onClose = null,
  persistStorageKey = null,
  sizePresets = FLOATING_MAP_SIZE_PRESETS,
} = {}) {
  const anchorPositionRef = React.useRef(null);
  const dragRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const resizeRef = React.useRef(null);
  const resolvedInitialPosition = React.useMemo(
    () => resolveInitialPosition(initialPosition),
    [initialPosition?.x, initialPosition?.y],
  );
  const resolvedInitialSize = React.useMemo(
    () => resolveInitialSize(initialSize, sizePresets),
    [initialSize, sizePresets],
  );
  const storedLayout = useUserUiPreferenceStorageValue("local", persistStorageKey || "");
  const persistedSnapshot = React.useMemo(
    () => (
      persistStorageKey
        ? readPersistedFloatingRouteMapState(persistStorageKey, { sizePresets })
        : null
    ),
    [persistStorageKey, sizePresets, storedLayout],
  );
  const hasCustomPositionRef = React.useRef(persistedSnapshot?.hasCustomPosition === true);
  const [isFloating, setIsFloating] = React.useState(() => persistedSnapshot?.isOpen === true);
  const [position, setPosition] = React.useState(() => (
    persistedSnapshot?.position
      ? resolveInitialPosition(persistedSnapshot.position)
      : resolvedInitialPosition
  ));
  const [size, setSize] = React.useState(() => (
    persistedSnapshot?.size
      ? resolveInitialSize(persistedSnapshot.size, sizePresets)
      : resolvedInitialSize
  ));
  const [isDragging, setIsDragging] = React.useState(false);
  const [isResizing, setIsResizing] = React.useState(false);
  const [customWidth, setCustomWidth] = React.useState(() => persistedSnapshot?.customWidth ?? null);
  const [customHeight, setCustomHeight] = React.useState(() => persistedSnapshot?.customHeight ?? null);
  const mountNode = typeof document !== "undefined" ? document.body : null;

  React.useEffect(() => {
    if (!persistStorageKey || dragRef.current || resizeRef.current) return;
    setIsFloating(persistedSnapshot?.isOpen === true);
    const nextPosition = persistedSnapshot?.position ? resolveInitialPosition(persistedSnapshot.position) : resolvedInitialPosition;
    setPosition((current) => current.x === nextPosition.x && current.y === nextPosition.y ? current : nextPosition);
    setSize(persistedSnapshot?.size ? resolveInitialSize(persistedSnapshot.size, sizePresets) : resolvedInitialSize);
    setCustomWidth(persistedSnapshot?.customWidth ?? null);
    setCustomHeight(persistedSnapshot?.customHeight ?? null);
    hasCustomPositionRef.current = persistedSnapshot?.hasCustomPosition === true;
  }, [persistStorageKey, persistedSnapshot]);

  const dimensions = React.useMemo(
    () => resolveFloatingMapDimensions(size, customWidth, customHeight, sizePresets),
    [customHeight, customWidth, size, sizePresets],
  );
  const panelStyle = React.useMemo(
    () => (isFloating
      ? {
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: `${dimensions.width}px`,
        }
      : undefined),
    [dimensions.width, isFloating, position.x, position.y],
  );

  const resetLayout = React.useCallback((nextSize = resolvedInitialSize) => {
    setPosition(resolvedInitialPosition);
    setSize(resolveInitialSize(nextSize, sizePresets));
    setCustomWidth(null);
    setCustomHeight(null);
    setIsDragging(false);
    setIsResizing(false);
    anchorPositionRef.current = null;
    dragRef.current = null;
    hasCustomPositionRef.current = false;
    resizeRef.current = null;
  }, [resolvedInitialPosition, resolvedInitialSize, sizePresets]);

  const closeFloating = React.useCallback(() => {
    if (persistStorageKey) {
      clearPersistedFloatingRouteMapState(persistStorageKey);
    }

    resetLayout(resolvedInitialSize);
    setIsFloating(false);
    onClose?.();
  }, [onClose, persistStorageKey, resetLayout, resolvedInitialSize]);

  const openFloating = React.useCallback((anchorElement = null, anchorPosition = null) => {
    resetLayout(resolvedInitialSize);
    anchorPositionRef.current = Number.isFinite(Number(anchorPosition?.x)) && Number.isFinite(Number(anchorPosition?.y))
      ? {
          x: Number(anchorPosition.x),
          y: Number(anchorPosition.y),
        }
      : resolveAbsoluteFloatingMapAnchorPosition(anchorElement);
    setIsFloating(true);
  }, [resetLayout, resolvedInitialSize]);

  const setSizePreset = React.useCallback((nextSize) => {
    if (!sizePresets[nextSize]) {
      return;
    }

    setSize(nextSize);
    setCustomWidth(null);
    setCustomHeight(null);
  }, [sizePresets]);

  React.useEffect(() => {
    if (!persistStorageKey || !isFloating) {
      return;
    }

    persistFloatingRouteMapState({
      customHeight,
      customWidth,
      hasCustomPosition: hasCustomPositionRef.current,
      isOpen: true,
      position,
      size,
    }, persistStorageKey);
  }, [customHeight, customWidth, isFloating, persistStorageKey, position, size]);

  React.useEffect(() => {
    if (!isFloating) {
      return;
    }

    const panelElement = panelRef.current;
    if (!(panelElement instanceof HTMLElement)) {
      return;
    }

    setPosition((currentPosition) => (
      hasCustomPositionRef.current
        ? clampFloatingMapDocumentPosition(currentPosition, panelElement)
        : resolveDefaultFloatingMapDocumentPosition(panelElement, anchorPositionRef.current)
    ));

    panelElement.focus();
  }, [customHeight, customWidth, isFloating, size]);

  const startDrag = React.useCallback((event) => {
    if (!isFloating || event.button !== 0) {
      return;
    }

    const panelElement = panelRef.current;
    if (!(panelElement instanceof HTMLElement)) {
      return;
    }

    event.preventDefault();
    try {
      panelElement.setPointerCapture?.(event.pointerId);
    } catch {
      // Ignore environments that do not expose an active pointer for capture.
    }

    dragRef.current = {
      originX: position.x,
      originY: position.y,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };
    setIsDragging(true);
  }, [isFloating, position.x, position.y]);

  const handlePointerMove = React.useCallback((event) => {
    const panelElement = panelRef.current;
    if (!(panelElement instanceof HTMLElement)) {
      return;
    }

    const dragState = dragRef.current;
    if (dragState && event.pointerId === dragState.pointerId) {
      hasCustomPositionRef.current = true;
      setPosition(clampFloatingMapDocumentPosition(
        {
          x: dragState.originX + (event.clientX - dragState.startX),
          y: dragState.originY + (event.clientY - dragState.startY),
        },
        panelElement,
      ));
      return;
    }

    const resizeState = resizeRef.current;
    if (!resizeState || event.pointerId !== resizeState.pointerId) {
      return;
    }

    const horizontalDelta = event.clientX - resizeState.startX;
    const verticalDelta = event.clientY - resizeState.startY;
    let nextWidth = resizeState.originWidth;
    let nextHeight = resizeState.originHeight;
    let nextX = resizeState.originX;
    let nextY = resizeState.originY;

    if (resizeState.direction.includes("east")) {
      nextWidth = clampFloatingMapWidth(resizeState.originWidth + horizontalDelta);
    }

    if (resizeState.direction.includes("west")) {
      nextWidth = clampFloatingMapWidth(resizeState.originWidth - horizontalDelta);
      nextX = resizeState.originX + (resizeState.originWidth - nextWidth);
      if (nextX < FLOATING_MAP_PANEL_INSET) {
        const overflow = FLOATING_MAP_PANEL_INSET - nextX;
        nextX = FLOATING_MAP_PANEL_INSET;
        nextWidth = clampFloatingMapWidth(nextWidth - overflow);
      }
    }

    if (resizeState.direction.includes("south")) {
      nextHeight = clampFloatingMapHeight(resizeState.originHeight + verticalDelta);
    }

    if (resizeState.direction.includes("north")) {
      nextHeight = clampFloatingMapHeight(resizeState.originHeight - verticalDelta);
      nextY = resizeState.originY + (resizeState.originHeight - nextHeight);
      if (nextY < FLOATING_MAP_PANEL_INSET) {
        const overflow = FLOATING_MAP_PANEL_INSET - nextY;
        nextY = FLOATING_MAP_PANEL_INSET;
        nextHeight = clampFloatingMapHeight(nextHeight - overflow);
      }
    }

    hasCustomPositionRef.current = true;
    setCustomWidth(nextWidth);
    setCustomHeight(nextHeight);
    setPosition((currentPosition) => (
      currentPosition.x === nextX && currentPosition.y === nextY
        ? currentPosition
        : { x: nextX, y: nextY }
    ));
  }, []);

  const handlePointerEnd = React.useCallback((event) => {
    const dragState = dragRef.current;
    if (dragState && event.pointerId === dragState.pointerId) {
      if (event.currentTarget instanceof HTMLElement && event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        try {
          event.currentTarget.releasePointerCapture(event.pointerId);
        } catch {
          // Ignore environments that already released the pointer.
        }
      }

      dragRef.current = null;
      setIsDragging(false);
      return;
    }

    const resizeState = resizeRef.current;
    if (!resizeState || event.pointerId !== resizeState.pointerId) {
      return;
    }

    if (event.currentTarget instanceof HTMLElement && event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // Ignore environments that already released the pointer.
      }
    }

    resizeRef.current = null;
    setIsResizing(false);
  }, []);

  const startResize = React.useCallback((event, direction = "south-east") => {
    if (!isFloating || event.button !== 0) {
      return;
    }

    const panelElement = panelRef.current;
    if (!(panelElement instanceof HTMLElement)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    try {
      panelElement.setPointerCapture?.(event.pointerId);
    } catch {
      // Ignore environments that do not expose an active pointer for capture.
    }

    const mapShellElement = panelElement.querySelector(".route-stage__map-shell");
    const originDimensions = resolveFloatingMapDimensions(size, customWidth, customHeight, sizePresets);

    resizeRef.current = {
      direction,
      originHeight: mapShellElement instanceof HTMLElement
        ? mapShellElement.getBoundingClientRect().height
        : originDimensions.mapHeight,
      originWidth: originDimensions.width,
      originX: position.x,
      originY: position.y,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };
    setIsResizing(true);
  }, [customHeight, customWidth, isFloating, position.x, position.y, size, sizePresets]);

  React.useEffect(() => {
    if ((!isDragging && !isResizing) || typeof window === "undefined") {
      return undefined;
    }

    const handleWindowPointerMove = (event) => {
      handlePointerMove(event);
    };
    const handleWindowPointerEnd = (event) => {
      handlePointerEnd(event);
    };

    window.addEventListener("pointermove", handleWindowPointerMove, true);
    window.addEventListener("pointerup", handleWindowPointerEnd, true);
    window.addEventListener("pointercancel", handleWindowPointerEnd, true);

    return () => {
      window.removeEventListener("pointermove", handleWindowPointerMove, true);
      window.removeEventListener("pointerup", handleWindowPointerEnd, true);
      window.removeEventListener("pointercancel", handleWindowPointerEnd, true);
    };
  }, [handlePointerEnd, handlePointerMove, isDragging, isResizing]);

  const handleKeyDown = React.useCallback((event) => {
    if (event.key !== "Escape") {
      return;
    }

    event.preventDefault();
    closeFloating();
  }, [closeFloating]);

  return {
    closeFloating,
    dimensions,
    handleKeyDown,
    handlePointerEnd,
    handlePointerMove,
    isDragging,
    isFloating,
    isResizing,
    mountNode,
    openFloating,
    panelRef,
    panelStyle,
    position,
    setSize: setSizePreset,
    size,
    startDrag,
    startResize,
  };
}
